/* eslint-disable react/jsx-no-target-blank */

import CheckIcon from '@mui/icons-material/Check'
import {
  styled,
  Dialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions,
  Typography,
} from '@mui/material'
import { setupApiGateway, ApiGatewayStatus } from 'api/systemSettings'
import LoadingButton from 'components/ui/LoadingButton'
import { LINK_TERMSOFUSE, REDIRECT_URLS } from 'config'
import { useAlert } from 'hooks/useDialog'
import useLogout from 'hooks/useLogout'
import { MouseEventHandler, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { useInitApiGatewayDialogLoaderData } from './InitApiGatewayDialog.loader'

const Button = styled(LoadingButton)({
  fontWeight: 'bold',
})

const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontSize: '1rem',
}))

const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  backgroundColor: 'rgb(186, 254, 227)',
  // ...
  paddingTop: `${theme.spacing(1.4)}!important`,
}))

interface Props {
  open: boolean
  useConcur?: boolean
  loading: boolean
  onCancelClick: MouseEventHandler<HTMLButtonElement>
  onConfirmClick: MouseEventHandler<HTMLButtonElement>
}

// TODO: fix reload error
const InitApiGatewayDialog = ({
  open,
  useConcur,
  loading,
  onCancelClick,
  onConfirmClick,
}: Props) => (
  <Dialog open={open}>
    <DialogTitle>初めて使うときの設定</DialogTitle>
    <DialogContent>
      <Typography variant="subtitle2">
        ご利用いただきありがとうございます。こちらより「利用開始」が行えます。
        <br />
        「利用開始」ボタンをクリックいただくことで、OCRの利用を開始いただけます。
        <br />
        {useConcur && (
          <p>
            またOCR結果をConcurへ送信する際には、
            <br />
            Concurシステムの取引先情報の連携が完了している必要がございます。
            <br />
            連携完了するまでは、OCR結果画面の表示ができませんのでご注意ください。
            <br />
            ※取引先情報の連携は、初回ログイン時に自動連携されています
            <br />
            ※連携の状態は、システム管理者権限のユーザーで設定画面よりご確認いただけます
          </p>
        )}
      </Typography>
      <DialogActions>
        <Button
          data-testid="cancel-button"
          pending={loading}
          onClick={onCancelClick}
          color="primary"
        >
          キャンセル
        </Button>
        <Button
          data-testid="start-button"
          pending={loading}
          onClick={onConfirmClick}
          startIcon={<CheckIcon />}
          variant="contained"
          color="primary"
        >
          利用開始
        </Button>
      </DialogActions>
    </DialogContent>
  </Dialog>
)

const InitGeneralApiGatewayDialog = ({
  open,
  useConcur: _,
  loading,
  onCancelClick,
  onConfirmClick,
}: Props) => (
  <Dialog open={open}>
    <DialogTitle>テキストと表の読み取り機能</DialogTitle>
    <DialogContent>
      <p>
        <Typography variant="subtitle2">ご利用いただきありがとうございます。</Typography>
      </p>
      <p>
        <Typography variant="subtitle2">
          画像内のテキストおよび表を抽出して読み取ることが可能になりました。
          <br />
          文書の「新規登録」画面より「テキスト・表」を選択で利用可能です。
        </Typography>
      </p>
      <p>
        <Typography variant="subtitle2">
          読み取り機能の提供開始にあたって、LINE WORKS OCR Reader利用規約の
          <br />
          内容を変更し、第4条を新設しました。新設された規定をご確認いただ
          <br />
          いた上で、この機能をご利用ください。
        </Typography>
      </p>
      <p>
        <Typography variant="subtitle2">
          ※注意：利用枚数に応じて費用が発生します。ご注意ください。
        </Typography>
      </p>
      <DialogActions>
        <Button
          data-testid="cancel-button"
          pending={loading}
          onClick={onCancelClick}
          color="primary"
        >
          キャンセル
        </Button>
        <Button
          data-testid="start-button"
          pending={loading}
          onClick={onConfirmClick}
          startIcon={<CheckIcon />}
          variant="contained"
          color="primary"
        >
          以下に同意して利用を開始する
        </Button>
      </DialogActions>
      <p style={{ textAlign: 'right' }}>
        <a href={LINK_TERMSOFUSE} target="_blank">
          LINE WORKS OCR Reader利用規約
        </a>
        に同意の上、ご利用ください
      </p>
    </DialogContent>
  </Dialog>
)

const trySetupApiGateway = async (useInvoice: boolean, useOrderForm) => {
  try {
    const result = await setupApiGateway(useInvoice, useOrderForm)
    return result as ApiGatewayStatus[]
  } catch {
    return null
  }
}

const InitApiGatewayDialogContainer = () => {
  const { generalStatus, invoiceStatus, orderFormStatus, useInvoiceOCR, useConcur, useOrderForm } =
    useInitApiGatewayDialogLoaderData()
  const alert = useAlert()
  const { logout, loggingOut } = useLogout()
  const navigate = useNavigate()

  // none: 未開始. failedの場合error?
  const openInvoice =
    (invoiceStatus === 'none' && useInvoiceOCR) || (orderFormStatus === 'none' && useOrderForm)
  const openGeneral = !openInvoice && generalStatus === 'none'

  const handleConfirmClick = async () => {
    const returnedStatus = await trySetupApiGateway(useInvoiceOCR, useOrderForm)

    if (
      returnedStatus !== null &&
      returnedStatus.every(
        ({ serviceName, status }) =>
          ['ocr_general', 'ocr_invoice_v2', 'ocr_order_form'].includes(serviceName) &&
          status === 'created',
      )
    ) {
      navigate(REDIRECT_URLS.HOME)
      alert({ message: ['OCR接続完了', 'OCRの利用接続を開始しました。'] })
    } else {
      await alert({
        message: ['OCR接続失敗', 'OCRの利用接続に失敗しました。', 'サポートへ問い合わせください。'],
      })
      return logout()
    }
  }

  useEffect(() => {
    if (invoiceStatus === 'failed' || generalStatus === 'failed' || orderFormStatus === 'failed') {
      alert({
        message: ['OCR接続失敗', 'OCRの利用接続に失敗しました。', 'サポートへ問い合わせください。'],
      }).then(() => logout())
    }
  }, [invoiceStatus, orderFormStatus, generalStatus, alert, logout])

  return (
    <>
      <InitApiGatewayDialog
        open={openInvoice}
        useConcur={useConcur}
        loading={loggingOut}
        onCancelClick={logout}
        onConfirmClick={handleConfirmClick}
      />
      <InitGeneralApiGatewayDialog
        open={openGeneral}
        loading={loggingOut}
        onCancelClick={logout}
        onConfirmClick={handleConfirmClick}
      />
    </>
  )
}

export default InitApiGatewayDialogContainer
